import { Injectable } from '@angular/core';
import { Leaflet } from '@common-modules/wlm-map/leaflet-export';
import * as turf from 'turf';
import { LayerType } from '../models/layer-type';
import { MapCoordinates } from '../models/map-coordinates';
import { NativeLayer } from '../models/native-layer';
import { ICON_DEFAULT_SIZE } from './generic-map-styles.service';

@Injectable()
export class GenericMapTooltipService {
  isClickedLayer(
    layerType: LayerType,
    clickLatLng: MapCoordinates,
    innerLayer: NativeLayer,
    map,
  ): boolean {
    const clickPoint = map.latLngToContainerPoint(clickLatLng);
    if (layerType === LayerType.Point && this.isClickOnPoint(clickPoint, innerLayer, map)) {
      return true;
    }

    if (this.isLineType(layerType) && this.isClickOnLine(clickPoint, innerLayer, map)) {
      return true;
    }

    if (layerType === LayerType.Polygon && this.isClickInPolygon(clickLatLng, innerLayer)) {
      return true;
    }

    return false;
  }

  isLineType = (layerType: LayerType) =>
    layerType === LayerType.Line ||
    layerType === LayerType.MultiLine ||
    layerType === LayerType.LineString;

  private isClickOnPoint(clickPoint, nativeLayer: NativeLayer, map) {
    const layerPoint = map.latLngToContainerPoint(nativeLayer.getLatLng());
    const radius = this.getLayerRadius(nativeLayer);
    const dx = clickPoint.x - layerPoint.x;
    const dy = clickPoint.y - layerPoint.y;
    return Math.sqrt(dx * dx + dy * dy) <= radius;
  }

  private isClickOnLine(clickPoint, nativeLayer: NativeLayer, map, tolerance = 2) {
    const coords: { lat; lng }[] = nativeLayer.getLatLngs();
    for (let i = 0; i < coords.length - 1; i++) {
      const a = map.latLngToContainerPoint(coords[i]);
      const b = map.latLngToContainerPoint(coords[i + 1]);
      if (this.isPointNearLine(clickPoint, a, b, tolerance)) {
        return true;
      }
    }
    return false;
  }

  private isPointNearLine(p, a, b, tolerance) {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const length = Math.sqrt(dx * dx + dy * dy);
    if (length === 0) {
      // If a and b are the same point, compare the direct distance
      const dist = Math.sqrt((p.x - a.x) ** 2 + (p.y - a.y) ** 2);
      return dist <= tolerance;
    }
    let dot = ((p.x - a.x) * dx + (p.y - a.y) * dy) / (length * length);
    // Limit dot to the range [0, 1] so that closest is on the segment
    dot = Math.max(0, Math.min(1, dot));
    const closest = {
      x: a.x + dot * dx,
      y: a.y + dot * dy,
    };
    const dist = Math.sqrt((p.x - closest.x) ** 2 + (p.y - closest.y) ** 2);
    return dist <= tolerance;
  }

  private isClickInPolygon(clickLatLng, nativeLayer: NativeLayer) {
    const coords: { lat; lng }[] = nativeLayer.getLatLngs();
    const poly = Leaflet.polygon(coords);
    return (
      poly.getBounds().contains(clickLatLng) && this.leafletPointInPolygon(clickLatLng, coords)
    );
  }

  private leafletPointInPolygon(latlng, coords) {
    try {
      const poly = turf.polygon([[...coords.map((c) => [c.lng, c.lat])]]);
      const pt = turf.point([latlng.lng, latlng.lat]);
      return turf.booleanPointInPolygon(pt, poly);
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  private getLayerRadius = (nativeLayer) => {
    const defaultRadius = ICON_DEFAULT_SIZE / 2;
    const weightRadius = nativeLayer.options.weight ? Number(nativeLayer.options.weight) / 2 : null;
    const finalRadius = weightRadius ?? nativeLayer.options.radius ?? defaultRadius;
    return Math.max(finalRadius, defaultRadius);
  };
}
